import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const About = () => {
  const stats = [
    { number: '100+', label: 'Artisans Supported' },
    { number: '50+', label: 'Craft Categories' },
    { number: '5+', label: 'Years of Heritage' },
    { number: '3500+ sq. m', label: 'Manufacturing Space' }
  ];

  return (
    <section className="py-10 xs:py-12 sm:py-16 md:py-20 bg-white relative">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="/images/workshop.jpg"
          alt="ANG Workshop"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
      </div>

      <div className="container relative mx-auto px-3 xs:px-4 sm:px-6 lg:px-8 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6 xs:gap-8 lg:gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-4 xs:space-y-6"
            >
              <h2 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold mb-3 xs:mb-4 sm:mb-6 text-white">
                About ANG
              </h2>
              <p className="text-sm xs:text-base sm:text-lg text-gray-100/70 mb-3 xs:mb-4 sm:mb-6 leading-relaxed">
                Founded in Bagru, Rajasthan, ANG is a family-run workshop that blends craftsmanship with scalable production.
                We serve brands, architects, and design studios across India and globally — offering flexibility in design, packaging, and sourcing.
                Every piece is a result of material honesty and thoughtful design.
              </p>
              <div className="space-y-3 xs:space-y-4 sm:space-y-6">
                <div className="flex items-start gap-2 xs:gap-3 sm:gap-4">
                  <div className="p-1.5 xs:p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                    <svg className="w-4 h-4 xs:w-5 xs:h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base xs:text-lg sm:text-xl font-semibold mb-1 xs:mb-1.5 sm:mb-2 text-white">Sustainable Practices</h3>
                    <p className="text-xs xs:text-sm sm:text-base text-gray-100/60 leading-relaxed">
                      We ensure all our products are created using eco-friendly materials and sustainable practices.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 xs:gap-3 sm:gap-4">
                  <div className="p-1.5 xs:p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                    <svg 
                      className="w-4 h-4 xs:w-5 xs:h-5 sm:w-6 sm:h-6 text-white" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="1.5" 
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base xs:text-lg sm:text-xl font-semibold mb-1 xs:mb-1.5 sm:mb-2 text-white">SEDEX Approved</h3>
                    <p className="text-xs xs:text-sm sm:text-base text-gray-100/60 leading-relaxed">
                      Our facility maintains the highest standards of ethical trade and responsible business practices, 
                      certified by SEDEX (Supplier Ethical Data Exchange).
                    </p>
                  </div>
                </div>
              </div>
        
              <div className="pt-2 xs:pt-3 sm:pt-4">
                <Link to="/about" className="block sm:inline-block">
                  <motion.button
                    className="btn btn-primary w-full sm:w-48 text-xs xs:text-sm sm:text-base py-2 xs:py-2.5 sm:py-3"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    More About ANG
                  </motion.button>
                </Link>
              </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-3 xs:gap-4 sm:gap-6"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="bg-white/10 backdrop-blur-sm p-3 xs:p-4 sm:p-6 rounded-lg xs:rounded-xl sm:rounded-2xl"
                  whileHover={{ y: -5 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h3 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-0.5 xs:mb-1 sm:mb-2">{stat.number}</h3>
                  <p className="text-xs xs:text-sm sm:text-base text-gray-100/50">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
